import React from 'react';
import {
  ApartmentOutlined,
  AppstoreOutlined,
  DeleteOutlined,
  EditOutlined,
  FlagOutlined,
  GiftOutlined,
  MessageOutlined,
  NotificationOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
  TagsOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { ScopeItem } from '../../services/scopeManagement';

type IconType = React.ComponentType<any>;

type GroupDef = {
  key: string;
  label: string;
  match: string[];
  Icon: IconType;
};

// Scopes for the deposit / withdraw / bank / report features, whose pages are gone
// from this app. The backend PERMISSION list and the per-tenant seed still own
// them, so `getAllScopes` keeps returning them — drop them here instead of
// offering permissions that gate nothing. No live scope name contains these
// keywords.
const REMOVED_SCOPE_KEYWORDS = ['DEPOSIT', 'WITHDRAW', 'BANK', 'REALTIME', 'PAYMENT', 'CONFIG', 'REPORT'];

const isRemovedScope = (name: string): boolean => {
  const upper = name.toUpperCase();
  return REMOVED_SCOPE_KEYWORDS.some((keyword) => upper.includes(keyword));
};

// Order matters: a scope joins the FIRST group whose keyword its name contains.
// Put more specific groups before broader ones (e.g. customer before user).
const GROUP_DEFS: GroupDef[] = [
  { key: 'affiliate', label: 'แนะนำเพื่อน', match: ['AFFILIATE'], Icon: ApartmentOutlined },
  { key: 'customer', label: 'ลูกค้า', match: ['CUSTOMER'], Icon: UserOutlined },
  { key: 'user', label: 'พนักงาน / สิทธิ์', match: ['REGISTER', 'EDIT-USER', 'MANAGE-SCOPE', 'USER'], Icon: TeamOutlined },
  { key: 'bonus', label: 'โบนัส', match: ['BONUS'], Icon: GiftOutlined },
  { key: 'game', label: 'เกม', match: ['GAME'], Icon: PlayCircleOutlined },
  { key: 'wallet', label: 'กระเป๋าเงิน', match: ['WALLET'], Icon: WalletOutlined },
  { key: 'ticket', label: 'Ticket', match: ['TICKET'], Icon: TagsOutlined },
  { key: 'quest', label: 'ภารกิจ', match: ['QUEST'], Icon: FlagOutlined },
  { key: 'banner', label: 'ระบบเว็บไซต์', match: ['BANNER'], Icon: NotificationOutlined },
  { key: 'chat', label: 'แชท / ฝนดาว', match: ['CHAT', 'RAIN'], Icon: MessageOutlined },
];

const OTHER_DEF: GroupDef = { key: 'other', label: 'อื่นๆ', match: [], Icon: AppstoreOutlined };

/**
 * Friendly Thai labels per scope.
 * - Page entries mirror the real menu `linkText` in src/config/routes.ts.
 * - Action/button entries are curated (no central registry exists for buttons).
 * Anything not listed falls back to the raw scope name.
 * NOTE: kept as a plain map (not imported from routesConfig) to avoid a
 * circular import: routes.ts -> EmployeeScope -> scopeGroups -> routes.ts.
 */
const SCOPE_LABELS: { [scopeName: string]: string } = {
  // wildcard
  'ALL': 'ทุกสิทธิ์ (ALL)',

  // pages (mirror routesConfig linkText)
  'WALLET-PAGE': 'กระเป๋าเงิน/เกมลูกค้า',
  'CUSTOMER-PAGE': 'รายชื่อลูกค้า',
  'REGISTER-PAGE': 'พนักงาน',
  'BANNER-PAGE': 'ระบบเว็บไซต์',
  'BANNER-PAGE-EDIT': 'แก้ไขระบบเว็บไซต์',
  'BONUS-PAGE': 'โบนัสลูกค้า',
  'TICKET-MANAGEMENT-PAGE': 'Ticket ลูกค้า',
  'GAME-PAGE': 'จัดการเกม',
  'QUEST-MANAGEMENT-PAGE': 'ตั้งค่าภารกิจ',
  'MANAGE-SCOPE-PAGE': 'จัดการ scope พนักงาน',
  'AFFILIATE_ORGCHART-PAGE': 'องค์กร (Affiliate)',
  'AFFILIATE-OVERVIEW-PAGE': 'ภาพรวมรายได้',
  'AFFILIATE-CAMPAIGN-PAGE': 'แคมเปญ',
  'AFFILIATE-QUEST-CREATE-PAGE': 'เพิ่มเควส',
  'AFFILIATE-QUEST-PROGRESS-PAGE': 'เช็คความคืบหน้าเควส',
  'AFFILIATE-COMMISSION-PAGE': 'ค้นหาคอมมิชชัน',

  // actions / buttons (curated)
  'REGISTER-CUSTOMER': 'สมัครสมาชิกลูกค้า',
  'EDIT-CUSTOMER': 'แก้ไขลูกค้า',
  'EDIT-CUSTOMER-STATUS': 'แก้ไขสถานะลูกค้า',
  'REGISTER-BUTTON': 'ปุ่มเพิ่มพนักงาน',
  'REGISTER-USER.OWNER': 'เพิ่มพนักงาน: Owner',
  'REGISTER-USER.DM': 'เพิ่มพนักงาน: DM',
  'REGISTER-USER.ADMIN+': 'เพิ่มพนักงาน: ADMIN+',
  'REGISTER-USER.ADMIN': 'เพิ่มพนักงาน: ADMIN',
  'EDIT-USER.OWNER': 'แก้ไขพนักงาน: Owner',
  'EDIT-USER.DM': 'แก้ไขพนักงาน: DM',
  'EDIT-USER.ADMIN+': 'แก้ไขพนักงาน: ADMIN+',
  'EDIT-USER.ADMIN': 'แก้ไขพนักงาน: ADMIN',
  'ADD-BONUS': 'เพิ่มโบนัส',
  'EDIT-BONUS': 'แก้ไขโบนัส',
  'ADD-TICKET': 'เพิ่ม Ticket',
  'AFFILIATE-CAMPAIGN-CREATE-BUTTON': 'ปุ่มสร้างแคมเปญ',
  'AFFILIATE-QUEST-CREATE-BUTTON': 'ปุ่มสร้างเควส',
  'AFFILIATE-QUEST-DETAIL-BUTTON': 'ปุ่มดูรายละเอียดเควส',
  'BAN-CHAT': 'แบนแชท',
  'BAN-RAIN': 'แบนฝนดาว',
};

/** Friendly label for a scope; falls back to the raw scope name if unmapped. */
export const getScopeLabel = (name: string): string => SCOPE_LABELS[name] || name;

export type GroupedScope = {
  key: string;
  label: string;
  Icon: IconType;
  pages: ScopeItem[];
  actions: ScopeItem[];
};

// Every page scope in this codebase (FE Scope enum, BE PERMISSION class, the
// migrations that seed a scope row, and the frozen allowlist) is spelled with
// "PAGE" as a suffix, e.g. "CUSTOMER-PAGE" - except BANNER-PAGE-EDIT, an action
// scope whose name happens to contain "PAGE" in the middle. A plain includes()
// check put it in the wrong bucket here; endsWith keeps every existing page
// scope classified the same way while routing that one to actions instead.
const isPageScope = (name: string): boolean => name.toUpperCase().endsWith('PAGE');

const findGroupKey = (name: string): string => {
  const upper = name.toUpperCase();
  const def = GROUP_DEFS.find((group) => group.match.some((keyword) => upper.includes(keyword)));
  return def ? def.key : OTHER_DEF.key;
};

/**
 * Group a flat scope list by page/feature using the config above.
 * Scopes for removed features are dropped. Within each group, scopes containing
 * "PAGE" become page-access toggles, the rest become action toggles. Empty groups
 * are omitted; group order follows GROUP_DEFS with "อื่นๆ" last.
 */
export const groupScopes = (scopes: ScopeItem[]): GroupedScope[] => {
  const buckets: { [key: string]: ScopeItem[] } = {};
  scopes.forEach((scope) => {
    if (isRemovedScope(scope.name)) return;
    const key = findGroupKey(scope.name);
    (buckets[key] = buckets[key] || []).push(scope);
  });

  const result: GroupedScope[] = [];
  [...GROUP_DEFS, OTHER_DEF].forEach((def) => {
    const items = buckets[def.key];
    if (!items || items.length === 0) return;
    result.push({
      key: def.key,
      label: def.label,
      Icon: def.Icon,
      pages: items.filter((s) => isPageScope(s.name)),
      actions: items.filter((s) => !isPageScope(s.name)),
    });
  });
  return result;
};

/** Pick an icon for an action/button scope based on its name. */
export const getActionIcon = (name: string): IconType => {
  const upper = name.toUpperCase();
  if (upper.includes('DELETE')) return DeleteOutlined;
  if (upper.includes('EDIT')) return EditOutlined;
  if (upper.includes('CREATE') || upper.includes('ADD') || upper.includes('BUTTON')) return PlusOutlined;
  if (upper.includes('BAN') || upper.includes('STOP')) return StopOutlined;
  if (upper.includes('REGISTER')) return UserAddOutlined;
  if (upper.includes('SCOPE')) return SafetyCertificateOutlined;
  return TagsOutlined;
};
